import type { CriterionStatus, FigureCategory, StatusMeta } from '../types';

export const FIG_CATEGORIES: Record<FigureCategory, string> = {
  banner: 'Banner',
  banner_mobile: 'Banner on mobile',
  homepage: 'Homepage',
  about: 'About page',
  grid: 'Thumbnail grid',
  video: 'Video thumbnail',
  other: 'Other',
};

export const STATUS: Record<CriterionStatus, StatusMeta> = {
  critical: { label: 'Critical', hint: 'Blocks the campaign' },
  conversion: { label: 'Conversion', hint: 'Improves ROI' },
  housekeeping: { label: 'Housekeeping', hint: 'Nice-to-have / strategic' },
  ok: { label: 'Pass', hint: 'Ready' },
  na: { label: 'N/A', hint: 'Not assessed' },
};

/** Order used to roll a category's criteria up to a single status. */
export const STATUS_ORDER: CriterionStatus[] = ['critical', 'conversion', 'housekeeping', 'ok', 'na'];

/** The three channel-level captures a new visitor sees. */
export const CHANNEL_CAP: { cat: FigureCategory; label: string }[] = [
  { cat: 'banner', label: 'Banner' },
  { cat: 'banner_mobile', label: 'Banner on mobile' },
  { cat: 'homepage', label: 'Homepage' },
  { cat: 'about', label: 'About page' },
];

export const COLLECT_TIP =
  'Deterministic set — same input every time, so two auditors get the same result. Capture: banner · homepage as it loads · About page · the top ~12 thumbnails as one strip · then 5 MOST-VIEWED + 5 MOST-RECENT videos (thumbnail, title, description, views, date). Do NOT pick videos you personally find interesting — that breaks repeatability.';

export interface ReportBlockDef {
  key: Extract<CriterionStatus, 'critical' | 'conversion' | 'housekeeping'>;
  tag: string;
  lead: string;
  why: string;
}

/** Report block model: critical→Must-fix, conversion→Should-fix, housekeeping→Nice-to-have. */
export const BLOCKS: ReportBlockDef[] = [
  {
    key: 'critical',
    tag: 'Must-fix',
    lead: 'Blocks the campaign — fix before any paid spend.',
    why: 'Each of these leaks paid traffic on the surface a new visitor lands on. Running a campaign before fixing them wastes budget.',
  },
  {
    key: 'conversion',
    tag: 'Should-fix',
    lead: 'Not a blocker, but each one lifts campaign ROI.',
    why: "The campaign will run without these, but conversion will be lower. Worth doing if budget and time allow — skipping them isn't fatal, just less effective.",
  },
  {
    key: 'housekeeping',
    tag: 'Nice-to-have',
    lead: 'Optimisation for later — safe to defer.',
    why: "These improve the channel's long-term health rather than campaign readiness. Do them after launch, or as an ongoing engagement.",
  },
];
